import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { getAuth, updatePassword, updateEmail, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import Breadcrumb from '../components/Breadcrumbs/Breadcrumb';
import DefaultLayout from '../layout/DefaultLayout';
import Spinner from '../../../ui/LoadingSpinner';
import PasswordSection from '../components/Settings/PasswordSection';
import EmailSection from '../components/Settings/EmailSection';
import TwoFASection from '../components/Settings/TwoFactorSection';
import CurrencySection from '../components/Settings/CurrencySection';
// import ProfilePictureSection from '../components/Settings/ProfilePictureSection';

type FormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  newEmail: string;
  is2FAEnabled: boolean;
  selectedCurrency: string;
};

const Settings = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [defaultCurrency, setDefaultCurrency] = useState('USD');

  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [auth]);


  const handleSubmit = useCallback(async (formData: FormData): Promise<void> => {
    try {
      if (!currentUser) throw new Error('User not authenticated');

      const {
        currentPassword,
        newPassword,
        confirmPassword,
        newEmail,
        selectedCurrency,
      } = formData;

      if (newPassword && newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (newPassword && currentPassword === newPassword) {
        throw new Error('New password must be different');
      }

      if (newPassword) {
        await updatePassword(currentUser, newPassword);
      }

      if (newEmail && newEmail !== currentUser.email) {
        await updateEmail(currentUser, newEmail);
      }

      if (selectedCurrency) {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          defaultCurrency: selectedCurrency,
        });
        setDefaultCurrency(selectedCurrency);
      }

      toast.success('Settings updated successfully');
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Something went wrong');
      }
    }
  }, [currentUser, db]);

  if (loading) {
    return (
      <DefaultLayout>
        <div className="mx-auto max-w-270 flex items-center justify-center h-[60vh]">
          <Spinner />
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <Toaster position="top-center" />
      <div className="mx-auto max-w-270 text-white">
        <Breadcrumb pageName="Settings" />

        <motion.div
          className="grid grid-cols-5 gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="col-span-5 xl:col-span-3 space-y-6">
            <motion.div
              className="rounded-lg border border-stroke bg-boxdark p-6 shadow-md"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-lg font-semibold mb-4">Security</h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = new FormData(e.currentTarget);
                  const data: FormData = {
                    currentPassword: form.get('currentPassword')?.toString() || '',
                    newPassword: form.get('newPassword')?.toString() || '',
                    confirmPassword: form.get('confirmPassword')?.toString() || '',
                    newEmail: form.get('newEmail')?.toString() || '',
                    is2FAEnabled: form.get('2faEnabled') === 'on',
                    selectedCurrency: form.get('defaultCurrency')?.toString() || defaultCurrency,
                  };
                  handleSubmit(data);
                }}
                className="space-y-6"
              >
                <PasswordSection />
                {currentUser && <EmailSection currentEmail={currentUser.email!} />}
                <TwoFASection />
                <CurrencySection defaultCurrency={defaultCurrency} setDefaultCurrency={setDefaultCurrency} />
                {/* {currentUser && <ProfilePictureSection user={currentUser} />} */}
                <div className="flex justify-end gap-4.5 mt-6">
                  <button
                    type="reset"
                    className="px-4 py-2 border rounded-md text-white bg-transparent shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border rounded-md text-white bg-primary shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Save
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </DefaultLayout>
  );
};

export default Settings;

